/**
 * Montagem de enderecos que respeitam a imobiliaria atual.
 *
 * O `BrowserRouter` roda com `basename` igual ao slug quando ele vem do
 * caminho, entao todo `<Link to>` e todo `navigate()` ja saem prefixados de
 * graca. O problema e o resto: `href` de ancora, `target='_blank'`, HTML gerado
 * como string para o mapa, `window.location` — nada disso passa pelo React
 * Router, e um `/property/123` cru manda o visitante para a raiz, ou seja, para
 * a imobiliaria original.
 *
 * Este arquivo e o unico lugar onde o endereco de uma imobiliaria e decidido.
 * Com subdominio o slug sai do caminho e vai para a origem — e e aqui que essa
 * troca acontece, sem que nenhuma das 45 chamadas espalhadas pelo site precise
 * saber disso.
 */

import { getTenantSlug } from 'common/tenant'

import { getCustomHostTenant } from './custom-host'
import { baseDomain, getHostRole, rootHost, usesSubdomain } from './host'
import { CLIENT_GATEWAY_LABEL, LANDING_LABEL } from './reserved'

/**
 * `true` quando o ambiente resolve a imobiliaria pelo subdominio.
 *
 * Reexportado aqui porque quem monta ou exibe endereco pergunta a este arquivo,
 * e nao ao modulo de host.
 */
export const usesTenantSubdomain = usesSubdomain

function currentOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : ''
}

function protocol(): string {
  return typeof window !== 'undefined' ? window.location.protocol : 'https:'
}

/**
 * Porta do endereco atual, quando ha uma.
 *
 * Em producao nao ha — o site roda em 443. Mas uma origem montada sem a porta
 * e uma origem errada em qualquer ambiente que use outra: o servidor de
 * desenvolvimento em `imobx.localhost:3001` mandaria todo link para a porta 80.
 */
function portSuffix(): string {
  const port = typeof window !== 'undefined' ? window.location.port : ''
  return port ? `:${port}` : ''
}

function platformOrigin(label: string): string {
  return `${protocol()}//${label}.${baseDomain()}${portSuffix()}`
}

/** Origem da imobiliaria original no endereco da plataforma. */
function platformRootOrigin(): string {
  const root = rootHost()
  return root ? `${protocol()}//${root}${portSuffix()}` : currentOrigin()
}

/**
 * `true` quando o visitante esta no dominio proprio desta imobiliaria
 * (Feature #538). Ali o slug nao vai nem no caminho nem num subdominio: a
 * origem atual ja e o site dela.
 */
function isCurrentCustomHost(slug: string | null | undefined): boolean {
  if (!slug || getHostRole() !== 'custom') return false
  return getCustomHostTenant()?.slug === slug
}

/**
 * Caminho absoluto ja com o prefixo da imobiliaria.
 *
 * `tenantPath('/property/123')` devolve `/imobx/property/123` enquanto o
 * ambiente resolve por caminho, e `/property/123` quando resolve por
 * subdominio — ali o slug ja esta na origem, e repeti-lo no caminho levaria a
 * uma rota que nao existe.
 */
export function tenantPath(path: string, slug?: string | null): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const resolved = slug === undefined ? getTenantSlug() : slug

  if (usesSubdomain() || isCurrentCustomHost(resolved)) return normalized

  if (!resolved) return normalized
  if (normalized === '/') return `/${resolved}`

  return `/${resolved}${normalized}`
}

/**
 * Origem do site de uma imobiliaria.
 *
 * Com subdominio, cada imobiliaria tem origem propria — e por isso que a tela
 * de sucesso do cadastro consegue mostrar o endereco de uma imobiliaria
 * diferente daquela em que o visitante esta.
 */
export function tenantOrigin(slug?: string | null): string {
  const resolved = slug === undefined ? getTenantSlug() : slug

  if (isCurrentCustomHost(resolved)) return currentOrigin()

  if (!usesSubdomain()) {
    // No dominio proprio a origem atual so serve a imobiliaria dona dele;
    // qualquer outra mora no endereco da plataforma.
    return getHostRole() === 'custom' ? platformRootOrigin() : currentOrigin()
  }

  if (resolved)
    return `${protocol()}//${resolved}.${baseDomain()}${portSuffix()}`

  return platformRootOrigin()
}

/**
 * Endereco da imobiliaria na plataforma, ignorando o dominio proprio.
 *
 * E para onde vao o painel, o login e o portal quando o visitante esta em
 * `www.imobx.com.br` (decisao D2 do plano 538): a sessao e o Keycloak moram no
 * endereco da plataforma, e o token fica no `localStorage` dessa origem.
 */
export function platformTenantUrl(path: string, slug: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`

  if (usesSubdomain()) {
    return `${protocol()}//${slug}.${baseDomain()}${portSuffix()}${normalized}`
  }

  return `${platformRootOrigin()}/${slug}${
    normalized === '/' ? '' : normalized
  }`
}

/**
 * URL absoluta, com origem.
 *
 * Serve para o que precisa do endereco inteiro: botao "ver meu site" que abre
 * em outra aba, campo de copiar endereco, compartilhamento.
 */
export function tenantUrl(path = '/', slug?: string | null): string {
  return `${tenantOrigin(slug)}${tenantPath(path, slug)}`
}

/**
 * Mesma URL sem o esquema, para exibir na tela.
 *
 * `vistamar.seulugar.imb.br` le melhor do que a URL completa num campo de
 * "este e o seu endereco".
 */
export function tenantDisplayUrl(path = '/', slug?: string | null): string {
  return stripScheme(tenantUrl(path, slug))
}

/**
 * Endereco da landing page da plataforma.
 *
 * `landingUrl()` leva a captacao e `landingUrl('/cadastro')` ao formulario de
 * cadastro de imobiliaria. Enquanto nao ha subdominio, as duas telas continuam
 * nas rotas antigas da raiz.
 */
export function landingUrl(path = '/'): string {
  const normalized = path.startsWith('/') ? path : `/${path}`

  if (usesSubdomain()) {
    return `${platformOrigin(LANDING_LABEL)}${
      normalized === '/' ? '' : normalized
    }`
  }

  const legacy =
    normalized === '/'
      ? '/para-imobiliarias'
      : `/para-imobiliarias${normalized}`

  return `${currentOrigin()}${legacy}`
}

export function landingDisplayUrl(path = '/'): string {
  return stripScheme(landingUrl(path))
}

/**
 * Endereco curto que leva o cliente ao painel da sua imobiliaria.
 *
 * E o link que vai no e-mail de boas-vindas e na tela de sucesso do cadastro: e
 * o unico registro duravel que o cliente guarda depois de criar a conta. Nao
 * concede acesso a nada — resolve o slug e redireciona para `/admin`, onde o
 * login continua sendo exigido.
 */
export function clientGatewayUrl(slug?: string | null): string {
  const resolved = slug === undefined ? getTenantSlug() : slug

  if (usesSubdomain() && resolved) {
    return `${platformOrigin(CLIENT_GATEWAY_LABEL)}/${resolved}`
  }

  return tenantUrl('/admin', resolved)
}

export function clientGatewayDisplayUrl(slug?: string | null): string {
  return stripScheme(clientGatewayUrl(slug))
}

/**
 * Endereco de uma imobiliaria que so usa o subdominio quando ele de fato ja
 * resolve — senao cai no endereco de caminho (`app.{baseDomain}/{slug}`, modo
 * `dual`), que nao depende de nenhum DNS/CDN provisionado a mais.
 *
 * Existe para os dois lugares que navegam o navegador de verdade logo depois
 * do cadastro (o redirect automatico do formulario e o gateway de cliente):
 * mostrar o endereco final na tela e uma coisa, mandar o visitante para um
 * host que ainda pode nao existir e outra. Ver o incidente de 2026-09-03.
 */
export function tenantUrlWithFallback(
  path: string,
  slug: string,
  subdomainReady: boolean
): string {
  if (subdomainReady || !usesSubdomain()) return tenantUrl(path, slug)

  const root = rootHost()
  if (!root) return tenantUrl(path, slug)

  const normalized = path.startsWith('/') ? path : `/${path}`
  const suffix = normalized === '/' ? '' : normalized
  return `${protocol()}//${root}${portSuffix()}/${slug}${suffix}`
}

/**
 * `true` quando o site de uma imobiliaria mora em outra origem que a atual.
 *
 * Importa para sessao: o token vive em `localStorage`, que e por origem. Levar
 * o usuario recem-cadastrado de `lp.seulugar.imb.br` direto para
 * `imobx.seulugar.imb.br/admin` o deixaria deslogado no primeiro segundo de uso
 * da plataforma.
 */
export function isCrossOriginTenant(slug?: string | null): boolean {
  const origin = currentOrigin()
  return Boolean(origin) && tenantOrigin(slug) !== origin
}

function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, '')
}
